/**
 * Event Ticket Rewards — admin service.
 *
 * An Event Ticket carries a reward voucher that may be minted as a "$0 placeholder" envelope
 * (reward_state='pending'): it proves a check-in but holds no redeemable value until an operator
 * assigns a real offer to it. These calls back the admin "Pending ticket rewards" report + the
 * assign action. Both RPCs are leadership-guarded server-side (superadmin/admin/manager).
 */

import { supabase } from "./supabase";
import type { UUID } from "../types/common.types";

export interface PendingTicketReward {
  token_id: UUID;
  ticket_code: string; // public TK- code
  voucher_id: UUID;
  voucher_code: string;
  email: string | null;
  contact_id: UUID | null;
  contact_name: string | null;
  event_id: UUID | null;
  event_title: string | null;
  reward_state: string;
  created_at: string;
}

/** List ticket reward vouchers still awaiting an offer/value assignment. */
export async function listPendingTicketRewards(
  eventId?: UUID | null,
  limit = 200,
): Promise<PendingTicketReward[]> {
  const { data, error } = await supabase.rpc("list_pending_ticket_rewards", {
    p_event_id: eventId ?? null,
    p_limit: limit,
  });
  if (error) {
    throw new Error(error.message);
  }
  return (data as PendingTicketReward[]) ?? [];
}

export interface AssignableOffer {
  id: UUID;
  offer_code: string;
  name: string;
  value_type: string | null;
  value_amount: number | null;
  is_non_monetary_reward?: boolean | null;
}

/**
 * Active offers eligible to be assigned to a pending ticket reward. Excludes "$0 placeholder" envelopes
 * (you assign a real offer TO a placeholder, never another placeholder). Mirrors the DB guard in
 * _assign_ticket_reward_core exactly: a positive-value offer OR a designated non-monetary reward (e.g. an
 * Event Drawing Entry / physical gift, value 0) — NOT value_type='FREE' alone.
 */
export async function getAssignableOffers(): Promise<AssignableOffer[]> {
  const { data, error } = await supabase
    .from("offers")
    .select(
      "id, offer_code, name, value_type, value_amount, is_placeholder, is_non_monetary_reward",
    )
    .eq("is_active", true)
    .or("status.is.null,status.eq.active")
    .or("is_placeholder.is.null,is_placeholder.eq.false")
    .or("value_amount.gt.0,is_non_monetary_reward.eq.true")
    .order("name");
  if (error) {
    throw new Error(error.message);
  }
  return (data as AssignableOffer[]) ?? [];
}

export interface AssignTicketRewardResult {
  voucher_id: UUID;
  assigned_value: number;
  reward_state: string;
  idempotent_replay?: boolean;
}

/**
 * Assign a real offer (its value_amount) to a pending ticket reward voucher.
 * The token's public TK- code never changes; only the linked voucher gains value + reward_state='assigned'.
 */
export async function assignTicketReward(
  tokenId: UUID,
  offerId: UUID,
  idempotencyKey?: string,
): Promise<AssignTicketRewardResult> {
  const actorId = (await supabase.auth.getUser()).data.user?.id ?? null;
  const { data, error } = await supabase.rpc("assign_ticket_reward", {
    p_token_id: tokenId,
    p_offer_id: offerId,
    p_actor_user_id: actorId,
    p_idempotency_key: idempotencyKey ?? null,
  });
  if (error) {
    throw new Error(error.message);
  }
  return data as AssignTicketRewardResult;
}
